/** @jsxImportSource @emotion/react */
import { CloseOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import React, { useState } from 'react'
import { Alert, Input } from 'antd'
import Text from 'src/components/ts-components/Text'
import { usePromoCode } from './usePromoCode'

/**
 * Division information interface
 */
interface Division {
  schedule_id: string
  single_payment?: boolean
}

/**
 * Coupon information interface
 */
interface CouponInfo {
  id: string
  code: string
  discount: number
  type: 'percentage' | 'amount'
  subtotal: number
  discount_amount: number
  max_uses: number
}

/**
 * Props interface for the PromoCodeSection component
 */
interface PromoCodeSectionProps {
  couponInfo?: CouponInfo | null
  setCouponInfo: (coupon: CouponInfo | null) => void
  division: Division
  isTeamRep: boolean
}

/**
 * 🔄 PromoCodeSection: Handles promo code input and validation
 * Provides input field, Apply/Clear buttons, and validation result alerts
 */
export const PromoCodeSection: React.FC<PromoCodeSectionProps> = ({
  couponInfo,
  setCouponInfo,
  division,
  isTeamRep,
}) => {
  console.log('🔄 PromoCodeSection: Rendering promo code section')

  const [promoCode, setPromoCode] = useState('')
  const { validateCoupon, promoCodeError, isPromoCodeValid } = usePromoCode({
    division,
    isTeamRep,
    promoCode,
    setCouponInfo,
  })

  /**
   * 🔄 handleClearCoupon: Clears the applied coupon
   */
  const handleClearCoupon = () => {
    console.log('🔄 handleClearCoupon: Clearing applied coupon')
    setCouponInfo(null)
    setPromoCode('')
  }

  /**
   * 🔄 handleApplyCoupon: Applies the promo code
   */
  const handleApplyCoupon = async () => {
    console.log('🔄 handleApplyCoupon: Applying promo code')
    if (!isPromoCodeValid && promoCode.trim()) {
      await validateCoupon()
    }
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        gap: 8px;
        align-self: stretch;
        margin-top: 16px;
      `}
    >
      <Text
        variant='paragraphSmall'
        color={colors.WHITE}
        weight='regular'
        style='align-self: stretch;'
      >
        Coupon Code
      </Text>

      <div
        css={css`
          display: flex;
          align-items: flex-end;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Input
          name='promoCode'
          placeholder='SUMMER10'
          type='text'
          autoComplete='off'
          autoCorrect='off'
          autoCapitalize='true'
          required
          value={promoCode}
          onChange={(e) => setPromoCode(e.target.value)}
          disabled={isPromoCodeValid}
        />

        {couponInfo?.id ? (
          <div
            css={css`
              display: flex;
              padding: 8px;
              justify-content: center;
              align-items: center;
              gap: 8px;
              border-radius: 4px;
              background-color: ${colors.ERROR_200};
              height: 40px;
              cursor: pointer;
              margin-bottom: 2px;
            `}
            onClick={handleClearCoupon}
          >
            <CloseOutlined />
          </div>
        ) : (
          <div
            css={css`
              display: flex;
              padding: 8px;
              padding-left: 16px;
              padding-right: 16px;
              justify-content: center;
              align-items: center;
              gap: 8px;
              border-radius: 4px;
              background: ${colors.PRIMARY};
              height: 40px;
              cursor: ${isPromoCodeValid ? 'not-allowed' : 'pointer'};
              margin-bottom: 2px;
              color: ${colors.SECONDARY_300};
              font-size: 14px;
              font-weight: 400;
            `}
            onClick={handleApplyCoupon}
          >
            Apply
          </div>
        )}
      </div>

      {promoCodeError && (
        <div
          css={css`
            width: 100%;
          `}
        >
          <Alert message={promoCodeError} type='error' />
        </div>
      )}

      {isPromoCodeValid && couponInfo && (
        <div
          css={css`
            width: 100%;
          `}
        >
          <Alert
            message={`Coupon Code Applied: You Saved ${
              couponInfo.type === 'percentage'
                ? `- ${couponInfo.discount}% off`
                : `- $${couponInfo.discount} off`
            }! ${
              division?.single_payment
                ? 'Your discount will appear in the next screen.'
                : ''
            }`}
            type='success'
          />
        </div>
      )}
    </div>
  )
}

export default PromoCodeSection
